import moment from 'moment';
import { ActionTree } from 'vuex';
import {
  EDocumentAction,
  EDocumentMutation,
} from '@signing-page/documents/store/document.constants';
import { alertNamespace, EAlertAction } from '@signing-page/alert/store/alert.constants';
import service from '@signing-page/api/documents/services/document.service';
import { DocumentHelper } from '@signing-page/helpers/document.helper';
import { IDocument } from '@signing-page/documents/types/document';
import { IDocumentRevision } from '@signing-page/documents/types/document-revision';
import { EDocumentStatus } from '@signing-page/documents/types/enums/document-status';
import { ERecipientStatus } from '@lib-shared/recipients/types/enums/recipient-status';
import { IRootState } from '@signing-page/store/store';
import { IDocumentState } from '@signing-page/documents/store/document.types';
import { IInviteRecipientInfo } from '@signing-page/signing/types/invite-recipient-info';
import { EFieldAction } from '@signing-page/fields/store/field.constants';
import { EDocumentEvent } from '@signing-page/documents/types/enums/document-event';
import { IUploadAttachment } from '@signing-page/attachments/types/upload-attachment';
import { AttachmentService } from '@signing-page/api/attachments/services/attachment.service';
import { IDocumentAttachment } from '@lib-shared/types/document-attachment';
import { translate } from '@/helpers/translation.helper';
import { IRecipient } from '@lib-shared/types/Recipient';
import { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';

export const actions: ActionTree<IDocumentState, IRootState> = {
  async [EDocumentAction.LoadDocument]({ commit, dispatch }) {
    commit(EDocumentMutation.SetLoading, true);
    try {
      const document: IDocument = await service.getDocument();
      dispatch(EDocumentAction.LoadDocumentSuccess, document);
    } catch (error) {
      dispatch(EDocumentAction.LoadDocumentFailed);
    }
  },
  async [EDocumentAction.LoadDocumentSuccess]({ commit, dispatch }, document: IDocument) {
    commit(EDocumentMutation.SetDocument, document);
    dispatch(EDocumentAction.ExpiredReload, document);
    commit(EDocumentMutation.SetLoading, false);
  },
  async [EDocumentAction.LoadDocumentFailed]({ commit, dispatch }) {
    commit(EDocumentMutation.SetLoading, false);
    commit(EDocumentMutation.SetError, true);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('failed-to-load-document'), {
      root: true,
    });
  },
  async [EDocumentAction.LoadDocumentRevisions]({ commit, dispatch }) {
    commit(EDocumentMutation.SetRevisionsLoading, true);
    try {
      const { documentRevisions } = await service.getDocumentRevisions();
      dispatch(EDocumentAction.LoadDocumentRevisionsSuccess, documentRevisions);
    } catch (error) {
      dispatch(EDocumentAction.LoadDocumentRevisionsFailed);
    }
  },
  [EDocumentAction.LoadDocumentRevisionsSuccess](
    { commit },
    documentRevisions: IDocumentRevision[]
  ) {
    commit(EDocumentMutation.SetDocumentRevisions, documentRevisions);
    commit(EDocumentMutation.SetRevisionsLoading, false);
  },
  [EDocumentAction.LoadDocumentRevisionsFailed]({ commit, dispatch }) {
    commit(EDocumentMutation.SetRevisionsLoading, false);
    commit(EDocumentMutation.SetError, true);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-load-document-revision'),
      {
        root: true,
      }
    );
  },

  async [EDocumentAction.SetRevisionViewedByRecipient]({ commit, dispatch }, revision: number) {
    commit(EDocumentMutation.SetRevisionsViewedLoading, true);
    try {
      await service.addRecipientView(revision);
      dispatch(EDocumentAction.SetRevisionViewedByRecipientSuccess);
    } catch (error) {
      dispatch(EDocumentAction.SetRevisionViewedByRecipientFailed);
    }
  },
  [EDocumentAction.SetRevisionViewedByRecipientSuccess]({ commit }) {
    commit(EDocumentMutation.SetRevisionsViewedLoading, false);
  },
  [EDocumentAction.SetRevisionViewedByRecipientFailed]({ commit, dispatch }) {
    commit(EDocumentMutation.SetRevisionsViewedLoading, false);
    commit(EDocumentMutation.SetError, true);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-set-revision-viewed'),
      {
        root: true,
      }
    );
  },
  async [EDocumentAction.LoadEditorRecipientInput]({ commit, dispatch }) {
    commit(EDocumentMutation.SetLoading, true);
    try {
      const editorRecipientInput: IEditorRecipientInput[] = await service.loadEditorRecipientInput();
      dispatch(EDocumentAction.LoadEditorRecipientInputSuccess, editorRecipientInput);
    } catch (err) {
      dispatch(EDocumentAction.LoadEditorRecipientInputFailed);
    }
  },

  [EDocumentAction.LoadEditorRecipientInputSuccess](
    { commit },
    editorRecipientInput: IEditorRecipientInput[]
  ) {
    commit(EDocumentMutation.SetEditorRecipientInput, editorRecipientInput);
    commit(EDocumentMutation.SetLoading, false);
  },

  [EDocumentAction.LoadEditorRecipientInputFailed]({ commit, dispatch }) {
    commit(EDocumentMutation.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-load-editor-recipient-input'),
      {
        root: true,
      }
    );
  },
  [EDocumentAction.SetEditorRecipientInput](
    { commit },
    editorRecipientInput: IEditorRecipientInput[]
  ) {
    commit(EDocumentMutation.SetEditorRecipientInput, editorRecipientInput);
  },

  async [EDocumentAction.SetFocusedRequiredId]({ commit }, focusedRequiredId: string) {
    commit(EDocumentMutation.SetFocusedRequiredId, focusedRequiredId);
  },
  [EDocumentAction.CompleteSigning](
    { commit, dispatch },
    { signeesWaiting, recipientId }: { signeesWaiting: number; recipientId: string }
  ) {
    const status: ERecipientStatus = ERecipientStatus.Signed;
    commit(EDocumentMutation.SetRecipientStatus, { recipientId, status });
    if (signeesWaiting === 0) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowInfo}`,
        translate('loading-document-certificate'),
        { root: true }
      );
      dispatch(EDocumentAction.SendPusherStatus, { status: EDocumentStatus.Signed });
      commit(EDocumentMutation.SetDocumentSignedDate, moment().toISOString());
    }
  },
  async [EDocumentAction.SetAttachmentView]({ commit }, id: string) {
    commit(EDocumentMutation.SetAttachmentView, id);
  },
  async [EDocumentAction.UpdateAttachments]({ commit }, document: IDocument) {
    const oldAttachments: IDocumentAttachment[] = (document && document.documentAttachments) || [];
    try {
      const documentAttachments: IDocumentAttachment[] = await service.getAttachments();
      commit(EDocumentMutation.SetDocumentAttachments, documentAttachments);
    } catch (error) {
      commit(EDocumentMutation.SetDocumentAttachments, oldAttachments);
    }
  },
  async [EDocumentAction.RejectDocument](
    _,
    {
      reason,
      recipientId,
      documentId,
      authId,
    }: { reason: string; recipientId: string; documentId: string; authId: string }
  ) {
    await service.reject(reason, documentId, recipientId, authId);
  },

  async [EDocumentAction.LoadAfterSign]({ dispatch }) {
    dispatch(EDocumentAction.LoadDocument);
    dispatch(`fields/${EFieldAction.Load}`);
  },
  async [EDocumentAction.SendDocumentStatus](
    { dispatch, state, rootState, commit },
    { newDocumentStatus }: { newDocumentStatus: EDocumentStatus }
  ) {
    if (rootState.isPreview) {
      return;
    }
    const {
      document: { recipients, status },
    }: IDocumentState = state;
    const recipient = DocumentHelper.getRecipientById(rootState.recipientId, recipients);

    if (status === newDocumentStatus && status === recipient.status) {
      return;
    }
    switch (newDocumentStatus) {
      case EDocumentStatus.Reviewed:
        await dispatch(EDocumentAction.SendPusherStatus, {
          status: EDocumentEvent.Reviewed,
        });
        await dispatch(EDocumentAction.SendEventLog, { event: EDocumentEvent.Reviewed });
        commit(EDocumentMutation.SetDocumentStatus, EDocumentStatus.Reviewed);
        break;
      case EDocumentStatus.Viewed:
        await dispatch(EDocumentAction.SendPusherStatus, {
          status: EDocumentEvent.Viewed,
        });
        commit(EDocumentMutation.SetDocumentStatus, EDocumentStatus.Viewed);
        break;
      case EDocumentStatus.Rejected:
        await dispatch(EDocumentAction.SendPusherStatus, {
          status: EDocumentEvent.Rejected,
        });
        await dispatch(EDocumentAction.SendEventLog, { event: EDocumentEvent.Rejected });
        commit(EDocumentMutation.SetDocumentStatus, EDocumentStatus.Rejected);
        break;
    }
  },
  async [EDocumentAction.SetRecipientStatus](
    { dispatch, rootState, state, commit },
    { status }: { status: ERecipientStatus }
  ) {
    const { recipientId }: IRootState = rootState;
    const {
      document: { recipients },
    }: IDocumentState = state;
    const recipient = DocumentHelper.getRecipientById(recipientId, recipients);
    switch (status) {
      case ERecipientStatus.Reviewed:
        commit(EDocumentMutation.SetRecipientStatus, {
          recipientId: recipient.id,
          status: ERecipientStatus.Reviewed,
        });
        await dispatch(EDocumentAction.SendPusherStatus, {
          status: EDocumentEvent.Reviewed,
        });
        await dispatch(EDocumentAction.SendEventLog, { event: EDocumentEvent.Reviewed });
        break;
      case ERecipientStatus.Viewed:
        commit(EDocumentMutation.SetRecipientStatus, {
          recipientId: recipient.id,
          status: ERecipientStatus.Viewed,
        });
        await dispatch(EDocumentAction.SendPusherStatus, {
          status: EDocumentEvent.Viewed,
        });
        break;
      case ERecipientStatus.Rejected:
        commit(EDocumentMutation.SetRecipientStatus, {
          recipientId: recipient.id,
          status: ERecipientStatus.Rejected,
        });
        await dispatch(EDocumentAction.SendPusherStatus, {
          status: EDocumentEvent.Rejected,
        });
        await dispatch(EDocumentAction.SendEventLog, { event: EDocumentEvent.Rejected });
        break;
    }
  },
  async [EDocumentAction.SendPusherStatus](
    { rootState, state },
    { status }: { status: EDocumentEvent }
  ) {
    try {
      const { authId, recipientId }: IRootState = rootState;
      const {
        document: { id: documentId, recipients },
      }: IDocumentState = state;
      const recipient = DocumentHelper.getRecipientById(recipientId, recipients);
      await service.pushStatusToPusher(documentId, recipient.id, authId, status);
    } catch (error) {
      console.error(error);
    }
  },
  async [EDocumentAction.SendEventLog]({ rootState, state }, { event }: { event: EDocumentEvent }) {
    try {
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = state;
      await service.logEvent(documentId, recipientId, authId, event);
    } catch (error) {
      console.error(error);
    }
  },
  async [EDocumentAction.UploadAttachment](
    { commit, dispatch },
    {
      uploadAttachment: { documentId, recipientId, authId, attachmentId, file },
      document,
    }: { uploadAttachment: IUploadAttachment; document: IDocument }
  ) {
    try {
      commit(EDocumentMutation.SetUploadingAttachment, attachmentId);
      await AttachmentService.upload(documentId, recipientId, authId, attachmentId, file);
      commit(EDocumentMutation.RemoveUploadingAttachment, attachmentId);
      dispatch(EDocumentAction.UpdateAttachments, document);
    } catch (e) {
      dispatch(EDocumentAction.UploadAttachmentFailed, attachmentId);
    }
  },
  async [EDocumentAction.UploadAttachmentFailed]({ commit, dispatch }, attachmentId: string) {
    commit(EDocumentMutation.RemoveUploadingAttachment, attachmentId);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-upload-attachment'),
      {
        root: true,
      }
    );
  },
  [EDocumentAction.ApproveDocument]({ commit }, { recipientId }: { recipientId: string }) {
    const status: ERecipientStatus = ERecipientStatus.Approved;
    commit(EDocumentMutation.SetRecipientStatus, { recipientId, status });
  },
  [EDocumentAction.ExpiredReload](_, { expirationDate }: IDocument) {
    const timeout: number = moment(expirationDate).diff(moment());
    const maxTimeout: number = 8 * 3600 * 1000;
    if (timeout > 0) {
      setTimeout(() => {
        window.location.reload();
      }, Math.min(timeout, maxTimeout));
    }
  },
  async [EDocumentAction.ForwardToRecipient](
    { rootState, state, dispatch, commit },
    recipientInfo: IInviteRecipientInfo
  ) {
    try {
      commit(EDocumentMutation.SetButtonLoading, true);
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = state;
      const response: { status: number; error: string } = await service.forward(
        documentId,
        recipientId,
        authId,
        recipientInfo
      );

      if (response.status === 1) {
        dispatch(EDocumentAction.ForwardToRecipientSuccess, recipientInfo);
      } else {
        dispatch(EDocumentAction.ForwardToRecipientFailed);
      }
    } catch (e) {
      dispatch(EDocumentAction.ForwardToRecipientFailed);
    }
  },
  async [EDocumentAction.ForwardToRecipientSuccess](
    { dispatch, commit },
    recipientInfo: IInviteRecipientInfo
  ) {
    dispatch(EDocumentAction.SendPusherStatus, { status: EDocumentEvent.Forwarded });
    dispatch(EDocumentAction.SendEventLog, { event: EDocumentEvent.Forwarded });
    if (recipientInfo.transferSignature) {
      dispatch(`fields/${EFieldAction.Load}`, { root: true });
    }
    dispatch(EDocumentAction.SetForwardStatus, true);

    const recipients: IRecipient[] = await service.getRecipients();
    commit(EDocumentMutation.UpdateRecipients, recipients);
    commit(EDocumentMutation.SetButtonLoading, false);
  },
  [EDocumentAction.ForwardToRecipientFailed]({ dispatch, commit }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-invite-recipient'),
      {
        root: true,
      }
    );
    commit(EDocumentMutation.SetButtonLoading, false);
  },
  [EDocumentAction.SetForwardStatus]({ commit }, forwarded: boolean) {
    commit(EDocumentMutation.SetForwarded, forwarded);
  },
  async [EDocumentAction.GetPdfUrl]({ dispatch }) {
    try {
      const pdfUrl: string = await service.getDownloadUrl();
      dispatch(EDocumentAction.GetPdfUrlSuccess, pdfUrl);
    } catch (e) {
      dispatch(EDocumentAction.GetPdfUrlFailed);
    }
  },
  [EDocumentAction.GetPdfUrlSuccess]({ commit }, pdfUrl: string) {
    commit(EDocumentMutation.SetPdfUrl, pdfUrl);
  },
  [EDocumentAction.GetPdfUrlFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('failed-to-download-pdf'), {
      root: true,
    });
  },
  [EDocumentAction.SetDocumentStatus]({ commit }, status: EDocumentStatus) {
    commit(EDocumentMutation.SetDocumentStatus, status);
  },
  async [EDocumentAction.DownloadDocument]({ commit, dispatch }) {
    commit(EDocumentMutation.SetIsDownloading, true);
    try {
      await service.download();
    } catch {
      dispatch(EDocumentAction.DownloadDocumentFailed);
    }
  },
  [EDocumentAction.DownloadDocumentComplete]({ commit }) {
    commit(EDocumentMutation.SetIsDownloading, false);
  },
  [EDocumentAction.DownloadDocumentFailed]({ dispatch, commit }) {
    commit(EDocumentMutation.SetIsDownloading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-download-document'),
      {
        root: true,
      }
    );
  },
};
